/**
 * TicketPickerModal — Iter 418.bf (Dirk, 2026-02-28)
 *
 * Same pattern as JobCardPickerModal — lists the customer's tickets,
 * newest first, filter box on top, single-click to pick.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Search, LifeBuoy } from "lucide-react";
import api, { formatErr } from "../lib/api";

export default function TicketPickerModal({
  open,
  customerId,
  onPick,
  onClose,
  excludeIds = [],
  title = "Pick a support ticket to link",
  subtitle = "Newest first · click one to link",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const excluded = useMemo(() => new Set(excludeIds || []), [excludeIds]);

  useEffect(() => {
    if (!open || !customerId) return;
    let alive = true;
    setLoading(true);
    setErr("");
    api.get(`/tickets?customer_id=${customerId}`)
      .then(({ data }) => {
        if (!alive) return;
        const rows = Array.isArray(data) ? data : (data?.items || []);
        rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setItems(rows);
      })
      .catch((e) => { if (alive) setErr(formatErr(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, customerId]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items
      .filter((t) => !excluded.has(t.id))
      .filter((t) => {
        if (!query) return true;
        const hay = [t.short_id, t.subject, t.description, t.status, t.priority].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(query);
      });
  }, [q, items, excluded]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="ticket-picker-backdrop"
    >
      <div className="hv-card w-full max-w-2xl overflow-hidden shadow-2xl" data-testid="ticket-picker-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div>
            <div className="font-semibold text-white flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-rose-300" /> {title}
            </div>
            <div className="text-[11px] text-[#a0aabf] mt-0.5">{subtitle}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-white/5 text-[#a0aabf] hover:text-white" data-testid="ticket-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-[#a0aabf]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by ticket number, subject…" className="hv-input pl-8 text-sm w-full" autoFocus data-testid="ticket-picker-search" />
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="ticket-picker-list">
          {loading && <div className="p-6 text-center text-sm text-[#a0aabf]">Loading tickets…</div>}
          {!loading && err && <div className="p-4 text-sm text-rose-300">{err}</div>}
          {!loading && !err && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-[#a0aabf]">
              {items.length === 0 ? "No tickets on this customer yet." : "No matches."}
            </div>
          )}
          {!loading && !err && filtered.map((t) => {
            const short = t.short_id
              ? (t.short_id.toUpperCase().startsWith("ST") ? t.short_id : `ST${t.short_id}`)
              : `ST${t.id.slice(0, 6)}`;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => onPick(t)}
                className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-rose-500/10 hover:border-rose-500/30 transition flex items-center gap-3"
                data-testid={`ticket-picker-item-${t.short_id || t.id.slice(0, 8)}`}
              >
                <div className="text-xs font-mono text-rose-300 min-w-[70px] font-semibold">{short}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{t.subject || "Untitled ticket"}</div>
                  <div className="text-[11px] text-[#a0aabf] mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="uppercase tracking-wider">{(t.status || "").replace(/_/g, " ") || "—"}</span>
                    {t.created_at && <span>· {new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
